/**
 * The desktop app's notification rules, kept pure so they read (and test) on
 * their own. Used by hooks/useDesktopNotifications.ts.
 */
import type { DesktopWindowState } from './desktop';

/**
 * Whether a feed event deserves a system notification: not when the person is
 * already looking at that very page — the rule the web's service worker
 * follows too (public/sw.js, isAlreadyLooking). Compared by path; the query
 * (e.g. ?cerere=) does not make it a different page.
 */
export function shouldNotify(
  eventUrl: string,
  window: DesktopWindowState,
  currentPathname: string,
): boolean {
  if (!window.focused || !window.visible) return true;
  const path = eventUrl.split(/[?#]/)[0];
  return path !== currentPathname;
}

/**
 * Where the feed cursor is kept: sessionStorage, so a reload of the window
 * (an instance update) picks up what arrived meanwhile, while a fresh start
 * of the app begins from "now" instead of replaying old notifications.
 */
export function feedCursorKey(userId: string): string {
  return `firmio.desktop.feedCursor.${userId}`;
}

/** The delay before retrying a failed feed request: 1 s, 2 s, 4 s… up to 30 s. */
export function feedRetryDelayMs(failures: number): number {
  return Math.min(30_000, 1000 * 2 ** Math.max(0, Math.min(failures - 1, 5)));
}
